import { Download, Link } from 'lucide-react';
import { ExerciseActivityTargetDTO } from '../../services/apiClient';
import Button from '../common/Button';
import Loader from '../common/Loader';
import { useExerciseActivityTargetApi } from '../../hooks/useExerciseActivityTargetApi';
import { showAlert } from '../../utils/sweetAlertUtils';
import { useState } from 'react';
import { downloadFileFromUrl } from '../../utils/downloadUtils';

interface DownloadActivityTargetsProps {
  activityTargets: ExerciseActivityTargetDTO[];
  isLoading: boolean;
  onClose: () => void;
}

const DownloadActivityTargets: React.FC<DownloadActivityTargetsProps> = ({
  activityTargets,
  isLoading,
  onClose,
}) => {
  const { downloadActivityTarget } = useExerciseActivityTargetApi();
  const [downloadingTargetId, setDownloadingTargetId] = useState<string | null>(null);

  const handleFileDownload = async (target: ExerciseActivityTargetDTO) => {
    if (!target.id) {
      await showAlert({
        icon: 'error',
        title: 'Error',
        text: 'Target information is missing',
      });
      return;
    }

    setDownloadingTargetId(target.id);
    try {
      // Generate activity target PDF with date stamp
      const sasUri = await downloadActivityTarget(target.id);
      if (sasUri) {
        // Download the PDF (handles mobile/PWA automatically)
        await downloadFileFromUrl(
          sasUri,
          `activity-target-${target.title}.pdf`,
          () => {
            console.log('Activity target downloaded successfully');
          },
          async (error) => {
            await showAlert({
              icon: 'error',
              title: 'Download Failed',
              text: 'Failed to download activity target PDF',
            });
          }
        );
      }
    } catch (error: any) {
      await showAlert({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to generate activity target PDF',
      });
    } finally {
      setDownloadingTargetId(null);
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {activityTargets.map((target) => (
            <div
              key={target.id}
              className="flex items-center justify-between p-4 my-5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{target.title}</h4>
              </div>

              <Button
                variant="primary"
                onClick={() => target.isUrl
                  ? downloadFileFromUrl(target.imageUrl!, `${target.title}.pdf`)
                  : handleFileDownload(target)
                }
                disabled={!target.isActive || downloadingTargetId === target.id}
              >
                {downloadingTargetId === target.id ? 'Generating...' : target.isUrl ? <Link /> : <Download />}
              </Button>
            </div>
          ))}
        </>
      )}

      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
};

export default DownloadActivityTargets;
